import {
  IntervalItem,
  IntervalWithValueItem,
  LegendConfig,
  LegendType,
  TextItem,
  Unit,
} from '@ais-ui/websockets';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useState } from 'react';
import { cn } from '@ais-ui/utils';
import { PiCaretUpLight, PiSquaresFour } from 'react-icons/pi';

interface LegendProps {
  legend: LegendConfig<LegendType>;
  className: string;
}

interface ItemIntervalWithValueProps {
  legend: LegendConfig<LegendType>;
  item: IntervalWithValueItem;
}

interface ItemIntervalProps {
  legend: LegendConfig<LegendType>;
  item: IntervalItem;
}

interface ItemTextProps {
  item: TextItem;
}

const getIntervalText = (item: IntervalWithValueItem | IntervalItem) => {
  if (item.start_interval !== item.end_interval) {
    return `${item.start_interval} - ${item.end_interval}`;
  }

  return item.start_interval;
};

const unitToText = (unit: Unit) => {
  switch (unit) {
    case 'count':
      return 'Count';
    case 'km/h':
      return 'Km/h';
    case 'degreesCelsius':
      return '°C';
    case 'percentage':
      return '%';
    case 'knots':
      return 'Knots';
    case 'meters':
      return 'Meters';
    case 'degrees':
      return '°';
    case 'm3':
      return 'm³';
  }
};

const getValueText = (item: IntervalWithValueItem) => {
  return parseFloat(item.value).toFixed(2);
};

const ItemIntervalWithValue = ({
  legend,
  item,
}: ItemIntervalWithValueProps) => {
  return (
    <div className="flex w-fit min-w-[140px] space-x-4">
      <div
        className="items-center rounded-md border px-2.5 py-0.5 h-6 w-6 border-gray-300"
        style={{ backgroundColor: item.color }}
      ></div>
      <div className="w-full flex justify-between space-x-4">
        <div>{getIntervalText(item)}</div>
        <div>
          {getValueText(item)} {unitToText(legend.unit)}
        </div>
      </div>
    </div>
  );
};

const ItemInterval = ({ legend, item }: ItemIntervalProps) => {
  return (
    <div className="flex w-fit min-w-[140px] space-x-4">
      <div
        className="items-center rounded-md border px-2.5 py-0.5 h-6 w-6 border-gray-300"
        style={{ backgroundColor: item.color }}
      ></div>
      <div className="w-full flex justify-between space-x-4">
        <div>
          {getIntervalText(item)} {unitToText(legend.unit)}
        </div>
      </div>
    </div>
  );
};

const ItemText = ({ item }: ItemTextProps) => {
  return (
    <div className="flex w-fit min-w-[140px] space-x-4">
      <div
        className="items-center rounded-md border px-2.5 py-0.5 h-6 w-6 border-gray-300"
        style={{ backgroundColor: item.color }}
      ></div>
      <div className="w-full flex justify-between space-x-4">
        <div>{item.text}</div>
      </div>
    </div>
  );
};

export const Legend = React.memo(({ legend, className }: LegendProps) => {
  const [gridColumnsClass, setGridColumnsClass] = useState('grid-cols-1');
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const updateGridColumns = () => {
      const maxItemsPerColumn = 15;
      const numberOfColumns = Math.ceil(
        legend.items.length / maxItemsPerColumn
      );

      setGridColumnsClass(`grid-cols-${numberOfColumns}`);
    };

    updateGridColumns();
  }, [legend.items.length]);

  const handleExpand = useCallback(() => {
    setExpanded(true);
  }, []);

  const handleCollapse = useCallback(() => {
    setExpanded(false);
  }, []);

  const renderLegendItems = useCallback(() => {
    if (legend.type === 'intervalWithValue') {
      return legend.items.map((item, index) => (
        <ItemIntervalWithValue
          key={index}
          legend={legend}
          item={item as IntervalWithValueItem}
        />
      ));
    }

    if (legend.type === 'interval') {
      return legend.items.map((item, index) =>
        !item.text ? (
          <ItemInterval key={index} legend={legend} item={item} />
        ) : (
          <ItemText key={index} item={item} />
        )
      );
    }

    return legend.items.map((item, index) => (
      <ItemText key={index} item={item} />
    ));
  }, [legend]);

  if (!expanded) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className={cn('p-1 hover:cursor-pointer', className)}
            onClick={handleExpand}
          >
            <PiSquaresFour className="h-6 w-6" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Expand legend</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Card className={cn('h-fit group/legend relative', className)}>
      <Button
        className="absolute -bottom-3 left-1/2 z-900 bg-background rounded-full transform border border-text-gray-900 text-gray-700 hover:bg-secondary h-6 w-6 p-0 invisible group-hover/legend:visible"
        onClick={handleCollapse}
      >
        <PiCaretUpLight className="h-4 w-4" />
      </Button>
      <CardHeader>
        <div className="flex flex-col space-y-1">
          <CardTitle className="font-medium text-lg">{legend.title}</CardTitle>
          {legend.subtitle && (
            <p className="text-muted-foreground text-sm">{legend.subtitle}</p>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className={cn('grid gap-y-2 gap-x-6', gridColumnsClass)}>
          {renderLegendItems()}
        </div>
      </CardContent>
    </Card>
  );
});

Legend.displayName = 'Legend';
